import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import useEmployeeFreeDrinks from '../../hooks/useEmployeeFreeDrinks';

// Maps backend error codes to descriptive German toast messages.
const errorMessageForCode = code => {
  switch (code) {
    case 'FREE_DRINK_QUOTA_EXCEEDED':
      return 'Dein Freigetränke-Kontingent ist aufgebraucht.';
    case 'FREE_DRINK_BRANCH_NOT_ASSIGNED':
      return 'Für diesen Standort bist du nicht freigegeben.';
    case 'FREE_DRINK_OUTSIDE_CURRENT_PERIOD':
      return 'Nur Einträge aus dem aktuellen Zeitraum können entfernt werden.';
    default:
      return 'Fehler beim Eintragen des Freigetränks.';
  }
};

// Lets any logged-in employee with an assigned branch log a consumed free drink and see
// the remaining quota for the current period.
const FreeDrinks = () => {
  const { getMyConsumption, logDrink, deleteEntry } = useEmployeeFreeDrinks();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [pendingDrink, setPendingDrink] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await getMyConsumption();
      setData(response);
      setSelectedBranchId(prev => {
        const branches = response?.branches || [];
        if (prev && branches.some(b => b.branchId === prev)) return prev;
        return branches.length ? branches[0].branchId : '';
      });
    } catch (error) {
      toast.error('Fehler beim Laden des Freigetränke-Kontingents.');
    } finally {
      setIsLoading(false);
    }
  }, [getMyConsumption]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLog = useCallback(
    async drinkKey => {
      if (!selectedBranchId) {
        toast.error('Bitte wähle zuerst einen Standort aus.');
        return;
      }
      setPendingDrink(drinkKey);
      try {
        await logDrink(drinkKey, selectedBranchId);
        toast.success('Freigetränk eingetragen.');
        await load();
      } catch (error) {
        toast.error(errorMessageForCode(error?.code));
      } finally {
        setPendingDrink(null);
      }
    },
    [logDrink, load, selectedBranchId]
  );

  // Deletion is a two-step action: the icon opens a confirmation modal, this runs on confirm.
  const confirmDelete = useCallback(async () => {
    if (!entryToDelete) return;
    setIsDeleting(true);
    try {
      await deleteEntry(entryToDelete.id);
      toast.success('Eintrag entfernt.');
      setEntryToDelete(null);
      await load();
    } catch (error) {
      toast.error(errorMessageForCode(error?.code));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteEntry, load, entryToDelete]);

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const branches = data?.branches || [];
  const groups = data?.groups || [];
  const entries = data?.entries || [];
  const periodCurrent = data?.periodLabelCurrent || 'aktuell';

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <LocalCafeIcon color='primary' />
        <Typography variant='h5' component='h1'>
          Freigetränke
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Trage hier deine in Anspruch genommenen Freigetränke ein. Dein Kontingent gilt{' '}
        {periodCurrent} und du kannst die Getränke frei wählen.
      </Typography>

      {branches.length === 0 ? (
        <Alert severity='info'>
          Dir ist aktuell kein Standort zugewiesen. Freigetränke können nur mit einem zugewiesenen
          Standort eingetragen werden.
        </Alert>
      ) : (
        <>
          {branches.length > 1 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id='free-drink-branch-label'>Standort</InputLabel>
              <Select
                labelId='free-drink-branch-label'
                label='Standort'
                value={selectedBranchId}
                onChange={event => setSelectedBranchId(event.target.value)}
              >
                {branches.map(branch => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.name || branch.branchId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Stack spacing={2}>
            {groups.map(group => {
              const remaining = group.remaining ?? 0;
              const exhausted = remaining <= 0;
              return (
                <Paper key={group.key} variant='outlined' sx={{ p: 2 }}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      {groups.length > 1 ? group.label : 'Dein Kontingent'}
                    </Typography>
                    <Chip
                      label={`noch ${remaining} von ${group.quota}`}
                      color={exhausted ? 'default' : 'success'}
                      size='small'
                    />
                  </Stack>
                  <Stack direction='row' spacing={1.5} flexWrap='wrap' useFlexGap>
                    {(group.drinks || []).map(drink => (
                      <Button
                        key={drink.key}
                        variant='contained'
                        disableElevation
                        disabled={exhausted || pendingDrink !== null}
                        onClick={() => handleLog(drink.key)}
                        startIcon={
                          pendingDrink === drink.key ? (
                            <CircularProgress size={16} color='inherit' />
                          ) : null
                        }
                      >
                        {drink.label}
                      </Button>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
            Deine Einträge ({periodCurrent})
          </Typography>
          {entries.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Noch keine Freigetränke eingetragen.
            </Typography>
          ) : (
            <List dense>
              {entries.map(entry => (
                <ListItem
                  key={entry.id}
                  secondaryAction={
                    <IconButton
                      edge='end'
                      aria-label='Eintrag entfernen'
                      onClick={() => setEntryToDelete(entry)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={entry.drinkLabel}
                    secondary={`${dayjs(entry.consumedAt).format('HH:mm')} Uhr${
                      entry.branchName ? ` · ${entry.branchName}` : ''
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}

      <Dialog
        open={!!entryToDelete}
        onClose={() => {
          if (!isDeleting) setEntryToDelete(null);
        }}
      >
        <DialogTitle>Freigetränk entfernen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {entryToDelete
              ? `Möchtest du den Eintrag „${entryToDelete.drinkLabel}“ wirklich entfernen? Dein Kontingent wird dadurch wieder freigegeben.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntryToDelete(null)} disabled={isDeleting}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmDelete}
            color='error'
            variant='contained'
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Entfernen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreeDrinks;
